import React, { useState }  from 'react';
import { useTranslation } from 'react-i18next';
import { Change } from '../../../models';
import { Alert, Button, Icon, Confirm, FieldHelp } from '../..';
import { RootState, useAppSelector, useAppDispatch } from '../../../redux';
import { toChangeStep, resolveChangeFromTriage, loadActiveTrack, loadChange } from '../../../redux';
import { Changes } from '../../../services';
import { formatMomentDate } from '../../../locales';

interface ProgressStepsProps {
    change: Change;
    onRemove: (values: AnyObj) => void;
    /** `actions`: compact status badge + workflow buttons on one row. */
    variant?: 'full' | 'actions';
}

function ProgressSteps(props: ProgressStepsProps) {
    const { change, onRemove, variant = 'full' } = props;
    const isActions = variant === 'actions';
    const showHistory = !isActions;

    const [showFinishConfirmModal, setShowFinishConfirmModal] = useState(false);
    const [showResolveConfirmModal, setShowResolveConfirmModal] = useState(false);
    const [trustBusy, setTrustBusy] = useState(false);

    const { t }    = useTranslation();
    const dispatch = useAppDispatch();

    const { user }   = useAppSelector((state: RootState) => state.auth);
    const { toStep } = useAppSelector((state: RootState) => state.changes);

    const goToProgressStep = async (step: string) => {
        await dispatch(toChangeStep(change._id, step, /*params*/{}, (err: Error | null) => {
            if ([Change.STATUS_IN_PROGRESS, Change.STATUS_FINISHED, Change.STATUS_CLIENT_REVIEW].includes(step))
                dispatch(loadActiveTrack());
        }));
    }

    const resolveWithoutStart = async () => {
        await dispatch(resolveChangeFromTriage(change._id, (err: Error | null) => {
            if (!err)
                dispatch(loadActiveTrack());
        }));
        setShowResolveConfirmModal(false);
    }

    const trustSpamSender = async (scope: 'email' | 'domain') => {
        if (!change?._id || trustBusy)
            return;
        setTrustBusy(true);
        try {
            await Changes.trustSpamSender(change._id, scope);
            dispatch(loadChange(change._id));
        } catch (err) {
            console.error(err);
        } finally {
            setTrustBusy(false);
        }
    };

    if (!change)
        return null;

    const isPendingTriage = change.progress?.status === Change.STATUS_PENDING_TRIAGE;
    const isSpamSuspect = change.progress?.status === Change.STATUS_SPAM_SUSPECT;
    const senderEmail = change.intake?.sender?.email || '';
    const senderDomain = change.intake?.sender?.domain
        || (senderEmail.includes('@') ? senderEmail.split('@').pop() : '');
    const isAssignee = change.assignee?._id === user?._id;
    const buttonBusy = toStep === 'pending' || trustBusy;

    const statusBadge = change.isPlannable() ? (
        <div className={isActions
            ? 'ml-2 inline-flex items-center gap-1 rounded-md bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:text-blue-200'
            : 'col-span-full rounded p-2 bg-blue-100 dark:bg-blue-900/40 border-blue-200 dark:border-blue-800 font-normal text-md text-center font-bold leading-6 text-blue-900 dark:text-blue-100'
        }>
            <Icon type="progress" className="inline mr-1" size={isActions ? 3 : undefined} color="current" />
            {t(change.progressStatusLabelKey())}
        </div>
    ) : (
        <div className={isActions
            ? 'ml-2 inline-flex items-center gap-1 rounded-md bg-yellow-100 dark:bg-yellow-900/40 px-2 py-0.5 text-xs font-semibold text-yellow-800 dark:text-yellow-200'
            : 'col-span-full rounded p-2 bg-yellow-100 dark:bg-yellow-900/40 border-yellow-200 dark:border-yellow-800 font-normal text-md text-center font-bold leading-6 text-yellow-900 dark:text-yellow-100'
        }>
            <Icon type="law" className="inline mr-1" size={isActions ? 3 : undefined} color="current" />
            {t(`quotes.status_type.${change.quote?.status || 'pending'}`)}
        </div>
    );

    const actionButtons = (
        <>
            {isSpamSuspect && (
                <div className={isActions
                    ? 'inline-flex flex-wrap items-center gap-1.5'
                    : 'col-span-full flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'
                }>
                    <div className="shrink-0">
                        <Button
                            icon={buttonBusy ? 'loading' : 'delete'}
                            color="danger"
                            small={isActions}
                            onClick={() => onRemove(change)}
                            title={t('changes.delete_this_change')}
                        />
                    </div>
                    <div className={isActions
                        ? 'inline-flex flex-wrap items-center gap-1.5'
                        : 'flex w-full min-w-0 flex-col gap-2 sm:max-w-md'
                    }>
                        <div className="flex items-center gap-1.5">
                            <Button
                                icon={buttonBusy ? 'loading' : 'thumbs-up'}
                                color="success"
                                small={isActions}
                                onClick={() => goToProgressStep(Change.STATUS_PENDING_TRIAGE)}
                                title={t('priorities.mark_safe')}
                                className={isActions ? undefined : 'w-full justify-start'}
                            />
                            {!isActions && (
                                <FieldHelp content={t('changes.help.mark_spam_safe') || ''} />
                            )}
                        </div>
                        {!!senderEmail && (
                            <div className="flex items-center gap-1.5">
                                <Button
                                    icon={buttonBusy ? 'loading' : 'email'}
                                    color="primary"
                                    small={isActions}
                                    onClick={() => trustSpamSender('email')}
                                    title={t('priorities.trust_sender')}
                                    className={isActions ? undefined : 'w-full justify-start'}
                                />
                                {!isActions && (
                                    <FieldHelp content={t('changes.help.trust_spam_sender') || ''} />
                                )}
                            </div>
                        )}
                        {!!senderDomain && (
                            <div className="flex items-center gap-1.5">
                                <Button
                                    icon={buttonBusy ? 'loading' : 'globe'}
                                    color="primary"
                                    small={isActions}
                                    onClick={() => trustSpamSender('domain')}
                                    title={t('priorities.trust_domain')}
                                    className={isActions ? undefined : 'w-full justify-start'}
                                />
                                {!isActions && (
                                    <FieldHelp content={t('changes.help.trust_spam_domain') || ''} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {isPendingTriage && (
                <div className={isActions
                    ? 'inline-flex flex-wrap items-center gap-1.5'
                    : 'col-span-full flex flex-col sm:flex-row items-center sm:justify-between gap-x1'
                }>
                    <div>
                        <Button
                            icon={buttonBusy ? 'loading' : 'delete'}
                            color="danger"
                            small={isActions}
                            onClick={() => onRemove(change)}
                            title={t('changes.delete_this_change')}
                        />
                    </div>
                    <div className="inline-flex flex-wrap items-center gap-1.5">
                        <div className="inline-flex items-center gap-1">
                            <Button
                                icon={buttonBusy ? 'loading' : 'check'}
                                color="success"
                                small={isActions}
                                onClick={() => setShowResolveConfirmModal(true)}
                                title={t('priorities.resolve')}
                            />
                            <FieldHelp content={t('changes.help.resolve') || ''} />
                        </div>
                        <div className="inline-flex items-center gap-1">
                            <Button
                                icon={buttonBusy ? 'loading' : 'check'}
                                color="success"
                                small={isActions}
                                onClick={() => goToProgressStep(Change.STATUS_PENDING_ESTIMATE)}
                                title={t('changes.accept_this_change')}
                            />
                            <FieldHelp content={t('changes.help.accept_triage') || ''} />
                        </div>
                    </div>
                    <Confirm
                        visible={showResolveConfirmModal}
                        title={t('changes.are_you_sure_to_resolve') || ''}
                        confirmText={t('changes.confirm_resolve') || ''}
                        onConfirm={resolveWithoutStart}
                        onCancel={() => setShowResolveConfirmModal(false)}
                    />
                </div>
            )}
            {change.isPlannable() && (
                <>
                    {(change.progress?.status === Change.STATUS_ASSIGNED) && (!change.progress.startedProgressAt) && (
                        <div className={isActions ? 'inline-flex items-center gap-1.5' : 'col-span-full flex flex-col items-center gap-x1'}>
                            {isAssignee ? (
                                <>
                                    <div className="inline-flex flex-wrap items-center justify-center gap-2">
                                        <div className="inline-flex items-center gap-1">
                                            <Button
                                                icon={buttonBusy ? 'loading' : 'check'}
                                                color="primary"
                                                small={isActions}
                                                onClick={() => goToProgressStep(Change.STATUS_IN_PROGRESS)}
                                                title={t('changes.start_progress')}
                                            />
                                            <FieldHelp content={t('changes.help.progress_actions') || ''} />
                                        </div>
                                        <div className="inline-flex items-center gap-1">
                                            <Button
                                                icon={buttonBusy ? 'loading' : 'check'}
                                                color="success"
                                                small={isActions}
                                                onClick={() => setShowResolveConfirmModal(true)}
                                                title={t('priorities.resolve')}
                                            />
                                            <FieldHelp content={t('changes.help.resolve') || ''} />
                                        </div>
                                    </div>
                                    <Confirm
                                        visible={showResolveConfirmModal}
                                        title={t('changes.are_you_sure_to_resolve') || ''}
                                        confirmText={t('changes.confirm_resolve') || ''}
                                        onConfirm={resolveWithoutStart}
                                        onCancel={() => setShowResolveConfirmModal(false)}
                                    />
                                </>
                            ) : (
                                <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                    <Alert title={t('changes.did_not_start_yet', {name: change.assignee?.firstname})} icon="info" color="info"/>
                                </label>
                            )}
                        </div>
                    )}
                    {(change.progress?.status === Change.STATUS_IN_PROGRESS) && (
                        <div className={isActions ? 'inline-flex items-center gap-1.5' : 'col-span-full flex flex-col items-center gap-x1'}>
                            {isAssignee && change.isDev() && (
                                <div className="inline-flex items-center gap-1">
                                    <Button
                                        icon={buttonBusy ? 'loading' : 'check'}
                                        color="primary"
                                        small={isActions}
                                        onClick={() => goToProgressStep(Change.STATUS_TEAM_REVIEW)}
                                        title={t('changes.start_team_review')}
                                    />
                                    <FieldHelp content={t('changes.help.progress_actions') || ''} />
                                </div>
                            )}
                            {isAssignee && !change.isDev() && (
                                <>
                                    <div className={`flex items-center gap-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 ${isActions ? '' : 'justify-between w-full'}`}>
                                        <Button
                                            icon={buttonBusy ? 'loading' : 'timer'}
                                            color="warning"
                                            small={isActions}
                                            onClick={() => goToProgressStep(Change.STATUS_CLIENT_REVIEW)}
                                            title={t('changes.start_waiting')}
                                        />
                                        <FieldHelp content={t('changes.help.progress_actions') || ''} />
                                        <Button
                                            icon={buttonBusy ? 'loading' : 'check'}
                                            color="success"
                                            small={isActions}
                                            onClick={() => setShowFinishConfirmModal(true)}
                                            title={t('changes.finish')}
                                        />
                                    </div>
                                    <Confirm
                                        visible={showFinishConfirmModal}
                                        title={t('changes.are_you_sure_to_finish') || ''}
                                        confirmText={t('changes.confirm_finish') || ''}
                                        onConfirm={() => goToProgressStep(Change.STATUS_FINISHED)}
                                        onCancel={() => setShowFinishConfirmModal(false)}
                                    />
                                </>
                            )}
                        </div>
                    )}
                    {(change.progress?.status === Change.STATUS_TEAM_REVIEW) && (
                        <div className={isActions ? 'inline-flex items-center gap-1.5' : 'col-span-full flex flex-col items-center gap-x1'}>
                            {isAssignee && (
                                <div className={`flex items-center gap-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 ${isActions ? '' : 'justify-between w-full'}`}>
                                    <Button
                                        icon={buttonBusy ? 'loading' : 'close'}
                                        color="warning"
                                        small={isActions}
                                        onClick={() => goToProgressStep(Change.STATUS_IN_PROGRESS)}
                                        title={t('changes.team_review_refused')}
                                    />
                                    <FieldHelp content={t('changes.help.progress_actions') || ''} />
                                    <Button
                                        icon={buttonBusy ? 'loading' : 'right'}
                                        color="primary"
                                        small={isActions}
                                        onClick={() => goToProgressStep(Change.STATUS_CLIENT_REVIEW)}
                                        title={t('changes.start_client_review')}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {(change.progress?.status === Change.STATUS_CLIENT_REVIEW) && (!change.progress.clientReviewAcceptedAt) && (
                        <div className={isActions ? 'inline-flex items-center gap-1.5' : 'col-span-full flex flex-col items-center gap-x1'}>
                            {isAssignee && change.isDev() && (
                                <div className={`flex items-center gap-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 ${isActions ? '' : 'justify-between w-full'}`}>
                                    <Button
                                        icon={buttonBusy ? 'loading' : 'close'}
                                        color="warning"
                                        small={isActions}
                                        onClick={() => goToProgressStep(Change.STATUS_IN_PROGRESS)}
                                        title={t('changes.client_review_refused')}
                                    />
                                    <FieldHelp content={t('changes.help.progress_actions') || ''} />
                                    <Button
                                        icon={buttonBusy ? 'loading' : 'check'}
                                        color="primary"
                                        small={isActions}
                                        onClick={() => goToProgressStep(Change.STATUS_PENDING_DEPLOYMENT)}
                                        title={t('changes.client_review_accepted')}
                                    />
                                </div>
                            )}
                            {isAssignee && !change.isDev() && (
                                <>
                                    <div className={`flex items-center gap-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 ${isActions ? '' : 'justify-between w-full'}`}>
                                        <Button
                                            icon={buttonBusy ? 'loading' : 'right'}
                                            color="primary"
                                            small={isActions}
                                            onClick={() => goToProgressStep(Change.STATUS_IN_PROGRESS)}
                                            title={t('changes.resume_from_waiting')}
                                        />
                                        <FieldHelp content={t('changes.help.progress_actions') || ''} />
                                        <Button
                                            icon={buttonBusy ? 'loading' : 'check'}
                                            color="success"
                                            small={isActions}
                                            onClick={() => setShowFinishConfirmModal(true)}
                                            title={t('changes.finish')}
                                        />
                                    </div>
                                    <Confirm
                                        visible={showFinishConfirmModal}
                                        title={t('changes.are_you_sure_to_finish') || ''}
                                        confirmText={t('changes.confirm_finish') || ''}
                                        onConfirm={() => goToProgressStep(Change.STATUS_FINISHED)}
                                        onCancel={() => setShowFinishConfirmModal(false)}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );

    const continueAlerts = (
        <>
            {/* Dev only: estimate/plan remain hard prerequisites. Non-dev can assign without them. */}
            {change.isDev() && !isPendingTriage && change.estimable && !(change.estimate > 0) && (
                <div className={isActions ? 'w-full basis-full' : 'col-span-full'}>
                    <Alert title={t('changes.estimate_to_continue')} icon="warning" color="warning"/>
                </div>
            )}
            {change.isDev() && !isPendingTriage && !change.estimable && change.progress?.status === Change.STATUS_PENDING_PLANIFICATION && (
                <div className={isActions ? 'w-full basis-full' : 'col-span-full'}>
                    <Alert title={t('changes.plan_to_continue')} icon="warning" color="warning"/>
                </div>
            )}
        </>
    );

    if (isActions) {
        return (
            <div className="flex flex-wrap items-center gap-2">
                {statusBadge}
                <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                    {actionButtons}
                </div>
                {continueAlerts}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
            {showHistory && change.progress?.startedProgressAt && (
            <div className="col-span-full flex flex-col gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 py-1">
                <label className="block text-xs font-light leading-6 text-gray-900 dark:text-gray-100">
                    <Icon type="check" color="current" className="inline mr-1.5" />
                    {t('changes.started_progress_at', {
                        date: formatMomentDate(change.progress.startedProgressAt, 'LL'),
                        time: formatMomentDate(change.progress.startedProgressAt, 'LT')
                    })}
                </label>
            </div>
            )}
            {showHistory && change.progress?.startedTeamReviewAt && (
            <div className="col-span-full flex flex-col gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 py-1">
                <label className="block text-xs font-light leading-6 text-gray-900 dark:text-gray-100">
                    <Icon type="check" color="current" className="inline mr-1.5" />
                    {t('changes.started_team_review_at', {
                        date: formatMomentDate(change.progress.startedTeamReviewAt, 'LL'),
                        time: formatMomentDate(change.progress.startedTeamReviewAt, 'LT')
                    })}
                </label>
            </div>
            )}
            {showHistory && change.progress?.startedClientReviewAt && (
            <div className="col-span-full flex flex-col gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 py-1">
                <label className="block text-xs font-light leading-6 text-gray-900 dark:text-gray-100">
                    <Icon type="check" color="current" className="inline mr-1.5" />
                    {t(change.isDev()
                        ? 'changes.started_client_review_at'
                        : 'changes.started_waiting_at', {
                        date: formatMomentDate(change.progress.startedClientReviewAt, 'LL'),
                        time: formatMomentDate(change.progress.startedClientReviewAt, 'LT')
                    })}
                </label>
            </div>
            )}
            {showHistory && change.progress?.clientReviewAcceptedAt && (
            <div className="col-span-full flex flex-col gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 py-1">
                <label className="block text-xs font-light leading-6 text-gray-900 dark:text-gray-100">
                    <Icon type="check" color="current" className="inline mr-1.5" />
                    {t('changes.client_review_accepted_at', {
                        date: formatMomentDate(change.progress.clientReviewAcceptedAt, 'LL'),
                        time: formatMomentDate(change.progress.clientReviewAcceptedAt, 'LT')
                    })}
                </label>
            </div>
            )}
            {showHistory && change.progress?.deployedAt && (
            <div className="col-span-full flex flex-col gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 py-1">
                <label className="block text-xs font-light leading-6 text-gray-900 dark:text-gray-100">
                    <Icon type="check" color="current" className="inline mr-1.5" />
                    {t('changes.deployed_at', {
                        date: formatMomentDate(change.progress.deployedAt, 'LL'),
                        time: formatMomentDate(change.progress.deployedAt, 'LT')
                    })}
                </label>
            </div>
            )}
            {showHistory && change.progress?.finishedAt && (
            <div className="col-span-full flex flex-col gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 py-1">
                <label className="block text-xs font-light leading-6 text-gray-900 dark:text-gray-100">
                    <Icon type="check" color="current" className="inline mr-1.5" />
                    {t('changes.finished_at', {
                        date: formatMomentDate(change.progress.finishedAt, 'LL'),
                        time: formatMomentDate(change.progress.finishedAt, 'LT')
                    })}
                </label>
            </div>
            )}

            {statusBadge}
            {actionButtons}
            {continueAlerts}
        </div>
    );
}

export default ProgressSteps;
